import { h, Fragment } from 'preact';
import { useMemo, useState } from 'preact/hooks';
import { formatMoney } from 'utils/storefront/format-resource';
import { trackMiniCartRemove, trackMiniCartProductClick } from 'utils/tracking';
import Image from 'utils/image';
import { Angle, Plus, Minus } from 'utils/icon/icons';
import CartProductPrice from 'utils/cart-product-price';
import GiftCardPrice from '../utils/gift-card-price';
import { withCartContext } from './cart-context';
import MaxOrderMessage from '../product/max-order-message';
import updateURL from '../utils/update-url';


const CartItem = ({
  cart,
  line,
  maxOrderQTY,
  isMobile,
  upsells
}) => {
  const [isBundleOpen, setIsBundleOpen] = useState(false);
  const upsellItem = upsells.find((u) => u.id === line.id);
  const isAutoAddUpsell = !!upsellItem?.type.includes('auto-add');
  const isUpsellItem = !!upsellItem;
  const upsellData = {
    appliedMsg: upsellItem?.appliedMsg,
    tooltipMsg: upsellItem?.tooltipMsg,
    showSelector: upsellItem?.showQuantitySelector,
    automaticallyDelete: upsellItem?.automaticallyDelete,
    maxQuantityAccessible: upsellItem?.maxQuantityAccessible,
    type: upsellItem?.type,
    showRemoveLink: upsellItem?.showRemoveLink,
  };

  const bundleItems = line.properties._bundle_data ? JSON.parse(line.properties._bundle_data) : { items: [] };
  const bundleItemsLength = bundleItems.items.length;

  const compareAtPrice = line.properties?._compare_price ? line.properties?._compare_price : 0;

  const hasLineLevelDiscount = line.line_level_discount_allocations && line.line_level_discount_allocations.length > 0;

  const discountedProductPercentageOff = line.original_price !== line.discounted_price ?
    Math.trunc(((line.original_price - line.discounted_price) / line.original_price) * 100)
    : null;

  const hasScriptDiscount =
    hasLineLevelDiscount && line.line_level_discount_allocations[0]?.discount_application?.type === 'script';

  const showPromoMessage =
    (isUpsellItem && upsellData?.appliedMsg && upsellData?.tooltipMsg && hasScriptDiscount) ||
    hasScriptDiscount ||
    isAutoAddUpsell;

  const hasVIPDiscount =
    line.line_level_discount_allocations &&
    !!line.line_level_discount_allocations[0]?.discount_application?.title?.includes('VIP');

  const scriptDiscountTitle =
    line.line_level_discount_allocations && line.line_level_discount_allocations[0]?.discount_application?.title;

  const maxQuantity = line.merchandise?.quantityAvailable || line.quantity_rule?.maxQuantity || Infinity;

  const isBundle = line.children;
  const isGiftCard = line.merchandise?.product?.handle === 'e-gift-card';
  let bundleValue = 0;
  let discountValue = null;

  const isMultiGwp = line.properties?._is_gwp_product || false;

  const nostoBundle = line.properties?._nosto_bundle;

  if (nostoBundle) {
    const BUNDLE_LIMIT = 3;
    discountValue = nostoBundle.discount.value;

    const nostoBundleItemsSize = cart.state.items.reduce((acc, rec) => {
      if (rec.properties?._nosto_bundle) {
        return acc + rec.quantity;
      }
      return acc;
    }, 0);

    if (nostoBundleItemsSize < BUNDLE_LIMIT) discountValue = null;
  }

  if (isBundle) {
    // Bundle value
    bundleValue = line.children?.reduce((total, child) => {
      const totalPrice = total + parseFloat(child.original_price / 100);
      return totalPrice;
    }, 0);
  }

  const handleAdjustQuantity = (quantity) => {
    let payload = [
      {
        id: line.key,
        quantity,
        properties: line.properties,
      },
    ];

    // If bundle, add children to update request
    if (isBundle) {
      payload = line.children.map((child) => ({
        id: child.id,
        properties: child.properties,
        quantity,
      }));
    }

    if (isUpsellItem && upsellData.type === 'auto-add-product-type' && quantity === 0) {
      setUpsellToStorage(line.id);
    }

    return cart.update(payload);
  };

  const handleClick = (e) => {
    e.preventDefault();

    if (isUpsellItem) return;

    trackMiniCartProductClick({
      item: isBundle ? line.merchandise.product.title : line.title,
      itemId: isBundle ? line.merchandise.sku : line.sku,
      text: 'Mini Cart',
    });
    // eslint-disable-next-line max-len
    window.location = updateURL(
      isBundle ? `${Shopify.routes.root}products/${line.merchandise.product.handle}` : line.url,
    );
  };

  const setUpsellToStorage = (id) => {
    const closedUpsellArr = JSON.parse(sessionStorage.getItem('ups_items') || '[]');

    if (!closedUpsellArr?.includes(id * 0.22)) {
      closedUpsellArr.push(id * 0.22);
      sessionStorage.setItem('ups_items', JSON.stringify(closedUpsellArr));
    }
  };

  const handleRemove = () => {
    let payload = [line.key];

    // If bundle, add children to update request
    if (isBundle) {
      payload = line.children.map((child) => child.id);
    }

    // when customer manually removes an auto-add GWP, store the item ID in
    // session storage, so that it doesn't get re-added automatically
    if (isUpsellItem && upsellData.type === 'auto-add-product-type') {
      setUpsellToStorage(line.key);
      sessionStorage.setItem('auto_add_product_type_removed', line.id);
    }

    if (isUpsellItem && upsellData.type === 'auto-add-threshold') {
      sessionStorage.setItem('auto_add_threshold_removed', line.id);
    }

    trackMiniCartRemove({
      item: isBundle ? line.merchandise.product.title : line.title,
      itemId: isBundle ? line.merchandise.sku : line.sku,
      quantity: line.quantity,
    });

    return cart.remove(payload);
  };

  const lineTitle = useMemo(() => {
    if (bundleItems.type) {
      return bundleItems.type;
    }
    return isBundle ? line.merchandise.product.title : line.title;
  }, [isBundle, line, bundleItems]);

  const lineItemImage = isBundle
    ? line.properties?._bundle_image || line.merchandise.product?.image?.src
    : line.properties?._bundle_image || line.image;

  let shortDescription = '';
  if (isBundle) {
    shortDescription = line.merchandise.product.subtitle;
  } else if (isUpsellItem) {
    shortDescription = upsellItem.subtitle;
  } else if (line.properties?._short_description) {
    shortDescription = line.properties?._short_description?.toLowerCase();
  }

  const BundlePrice = () =>
    bundleValue > line.merchandise.product.priceRange.minVariantPrice.amount ? (
      <div className="flex">
        <s className="text-grey-2">
          {formatMoney({
            amount: bundleValue,
          })}
        </s>
        <span className="text-red ml-2">{formatMoney(line.merchandise.product.priceRange.minVariantPrice)}</span>
      </div>
    ) : (
      <div>
        {formatMoney({
          amount: line.merchandise.product.priceRange.minVariantPrice.amount,
        })}
      </div>
    );

  const LinkElement = (props) => {
    if (!isUpsellItem && bundleItemsLength < 1) {
      return <a {...props}>{props.children}</a>;
    }
    // eslint-disable-next-line no-else-return
    else if (bundleItemsLength > 0) {
      return <span className={props.className}>{props.children}</span>;
    } else {
      return <span {...props}>{props.children}</span>;
    }
  };

  const toggleTooltip = (e) => {
    const tooltipElement = e.target.closest('[data-cart-item]').querySelector('[data-tooltip]');
    if (isMobile) {
      tooltipElement?.classList.toggle('opacity-100');
      tooltipElement?.classList.toggle('opacity-0');
    }
  };

  const SaleIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="13" fill="none" aria-hidden="true" aria-label="sale tag">
      <path
        fill="#fff"
        d="M6.15 1.3h4.96c.19 0 .35.16.35.35v4.96c0 .19-.07.36-.2.49L6.36 12c-.27.27-.72.27-.99 0L.75 7.39a.706.706 0 0 1 0-.99l4.9-4.9c.13-.13.31-.2.49-.2h.01Z"
      />
      <path
        fill="#000"
        d="M5.86 12.75c-.33 0-.65-.13-.88-.37L.36 7.77C.12 7.53 0 7.22 0 6.89c0-.33.13-.65.37-.88l4.9-4.9c.24-.24.55-.36.88-.36h4.96c.49 0 .89.4.89.89V6.6c0 .33-.13.65-.36.88l-4.9 4.9c-.24.24-.55.37-.88.37Zm.29-10.91s-.08.02-.11.05l-4.9 4.89c-.06.06-.06.16 0 .22l4.61 4.61c.05.05.17.05.22 0l4.9-4.9s.04-.07.04-.11V1.84H6.15Z"
      />
      <path fill="#000" d="M8.84 4.5a.76.76 0 1 0 0-1.52.76.76 0 0 0 0 1.52Z" />
    </svg>
  );

  const getCompareAtDiscountPercent = (_compareAtPrice, discountedPrice) => {
    const compareAt = parseFloat(_compareAtPrice);
    const discounted = parseFloat(discountedPrice) / 100;

    const discountPercent = Math.trunc(((compareAt - discounted) / compareAt) * 100);

    return discountPercent;
  };

  const PriceBlock = ({ className }) => (
    <div className={`leading-none ${className}`}>
      {!isBundle && !isGiftCard && !isMultiGwp && <CartProductPrice line={line} />}
      {isBundle && <BundlePrice />}
      {isGiftCard && <GiftCardPrice price={line.price} />}
    </div>
  );

  const PromoBadge = () => {
    const badgeText = line.properties?._cart_drawer_item_body_promo_badge;
    if (!badgeText) return null;

    return (
      <div
        data-cart-element="promo-badge"
        className="w-fit
          rounded-full
          text-[9px] font-medium leading-[11px] tracking-[.54px]
          bg-grey-11
          px-[6px] py-1
          mt-4
          uppercase"
      >
        <span>{badgeText}</span>
      </div>
    );
  };

  const lineItem = useMemo(
    () => (
      <div
        key={line.id}
        className="flex gap-3 md:gap-4 py-3 md:py-4 border-b border-grey-4 last-of-type:border-0"
        data-cart-item
      >
        {/* product image */}
        <LinkElement
          href={!isUpsellItem && !isMultiGwp ? line.properties._bundle_url || line.url : undefined}
          className={`basis-[116px] max-w-[116px] md:basis-[136px] md:max-w-[136px] xs:h-[116px] md:!h-[136px]
            relative ${bundleItemsLength < 1 && 'cursor-pointer'}`}
          onClick={!isUpsellItem && !isMultiGwp ? (e) => handleClick(e, line) : undefined}
        >
          <Image src={lineItemImage} classes="w-full object-contain rounded-[4px]" alt={`${line.title} image`} />

          {/* discount percent badge */}
          {compareAtPrice > 0 || discountedProductPercentageOff ? (
            <div
              data-cart-element="discount-badge"
              className="ab-tasty-cart-badge
              absolute top-[6px] left-[6px]
              w-fit
              rounded-full
              text-[9px] font-medium leading-[11px] tracking-[.54px]
              bg-grey-11
              px-[6px] py-1
              uppercase"
            >
              <span>
              {compareAtPrice ?
                getCompareAtDiscountPercent(compareAtPrice, line.price)
              : discountedProductPercentageOff
              }
              % OFF</span>
            </div>
          )
          : null}
        </LinkElement>

        {/* product details */}
        <div className="flex flex-col justify-between w-full max-w-[223px] md:max-w-[356px] md:relative">
          <div>
            <div className="md:flex md:justify-between">
              <LinkElement
                href={!isUpsellItem && !isMultiGwp ? line.properties._bundle_url || line.url : undefined}
                className={`${bundleItemsLength < 1 && 'cursor-pointer hover:underline'}`}
                onClick={!isUpsellItem && !isMultiGwp ? (e) => handleClick(e, line) : undefined}
              >
                <h4
                  className={`leading-110p tracking-1p mb-1 ${
                    !isUpsellItem && bundleItemsLength < 1 ? 'hover:underline' : ''
                  }`}
                >
                  {lineTitle}
                </h4>
                {shortDescription && (
                  <p
                    className={`capitalize text-[14px] leading-110p tracking-1p mb-4 md:mb-0 ${
                      !isUpsellItem && bundleItemsLength < 1 ? 'hover:underline' : ''
                    }`}
                  >
                    {shortDescription}
                  </p>
                )}
                {bundleItemsLength > 0 && (
                  <ul className="bundle-components">
                    {bundleItems.items.map((i) => (
                      <li key={i}>
                        <p>
                          {i.title} - {i.qty}
                        </p>
                      </li>
                    ))}
                  </ul>
                )}
              </LinkElement>

              <PriceBlock className="hidden md:block" />
            </div>

            <PriceBlock className="flex items-center gap-3 md:hidden" />
          </div>

          {!!showPromoMessage && (
            <div className="relative mb-[15px] group">
              <div className="flex items-center justify-start cursor-pointer">
                <SaleIcon />
                <p className="inline-block mx-1 text-sm">
                  {hasVIPDiscount ? scriptDiscountTitle : upsellData?.appliedMsg}
                </p>
                <button className="bg-transparent border-none" type="button" onClick={(e) => toggleTooltip(e)}>
                  <svg
                    className="hover:cursor-pointer"
                    xmlns="http://www.w3.org/2000/svg"
                    width="13"
                    height="14"
                    viewBox="0 0 13 14"
                    fill="none"
                    role="presentation"
                    focusable="false"
                    aria-label="info circle"
                    aria-hidden="true"
                  >
                    <title>info circle</title>
                    <path
                      d="M6.5 0.25C2.91379 0.25 0 3.16379 0 6.75C0 10.3362 2.91379
                        13.25 6.5 13.25C10.0862 13.25 13 10.3362 13 6.75C13 3.16379 10.0862
                        0.25 6.5 0.25ZM6.5 0.922414C9.71638 0.922414 12.3276 3.53362 12.3276
                        6.75C12.3276 9.96638 9.71638 12.5776 6.5 12.5776C3.28362 12.5776 0.672414
                        9.96638 0.672414 6.75C0.672414 3.53362 3.28362 0.922414 6.5 0.922414ZM6.5
                        3.83621C6.30948 3.83621 6.16379 3.9819 6.16379 4.17241V4.73276C6.16379
                        4.92328 6.30948 5.06897 6.5 5.06897C6.69052 5.06897 6.83621 4.92328 6.83621
                        4.73276V4.17241C6.83621 3.9819 6.69052 3.83621 6.5 3.83621ZM6.5
                        6.07759C6.30948 6.07759 6.16379 6.22328 6.16379 6.41379V9.32759C6.16379
                        9.5181 6.30948 9.66379 6.5 9.66379C6.69052 9.66379 6.83621 9.5181 6.83621
                        9.32759V6.41379C6.83621 6.22328 6.69052 6.07759 6.5 6.07759Z"
                      fill="black"
                    />
                  </svg>
                </button>
              </div>

              <p
                data-tooltip
                className={`absolute
                    bottom-[25px]
                    left-[25px]
                    max-w-[160px]
                    opacity-0
                    bg-black
                    text-white
                    text-sm
                    rounded-md
                    transition-opacity
                    ease-in-out
                    duration-75
                    ${
                      isMobile
                        ? 'py-[10px] pl-[10px] pr-[26px]'
                        : 'p-[10px] z-[-1] group-hover:z-auto group-hover:opacity-100'
                    }
                    `}
              >
                {hasVIPDiscount ? scriptDiscountTitle : upsellData?.tooltipMsg}
                {isMobile && (
                  <button type="button" onClick={(e) => toggleTooltip(e)}>
                    <svg
                      className="absolute top-[10px] right-[10px]"
                      xmlns="http://www.w3.org/2000/svg"
                      width="14"
                      height="14"
                      viewBox="0 0 14 14"
                      fill="none"
                      role="presentation"
                      focusable="false"
                      aria-label="close the message"
                      aria-hidden="true"
                    >
                      <path
                        d="M1.41075 1.28228L12.0987 11.9702M1.04639 12.2005L12.2464 1.00049"
                        stroke="white"
                        strokeWidth="2"
                        strokeLinecap="round"
                      />
                    </svg>
                  </button>
                )}

                <div role="presentation" className="absolute bottom-0 left-[50%] translate-x-[-50%]">
                  <div className="h-3 w-3 origin-bottom-left rotate-45 border border-black bg-black"></div>
                </div>
              </p>
            </div>
          )}

          <PromoBadge />

          {/* quantity selector */}
          <div className="flex justify-between items-center w-full mt-4 md:mt-6">
            {((!discountValue && !isUpsellItem) || (isUpsellItem && upsellData.showSelector)) && (
              <div>
                <div className={`flex justify-center w-[75px] h-8 border border-grey-11 rounded-[25px]`}>
                  <button
                    aria-label={window.TMW.Strings.product.decreaseQuantity}
                    className="flex justify-center items-center w-5 focus:ring-offset-0 [&_svg_line]:stroke-brown-1 [&:disabled_svg_line]:stroke-grey-8"
                    onClick={() => handleAdjustQuantity(line.quantity - 1)}
                  >
                    <Minus />
                  </button>

                  <input
                    type="number"
                    value={line.quantity > maxQuantity ? maxQuantity : line.quantity}
                    onBlur={(e) => handleAdjustQuantity(parseInt(e.target.value, 10))}
                    aria-label={window.TMW.Strings.product.quantityInput}
                    className="border-0 w-[18px] text-center text-[13px] px-0 text-brown-1"
                    max={maxQuantity}
                  />

                  <button
                    onClick={() => handleAdjustQuantity(line.quantity + 1)}
                    aria-label={window.TMW.Strings.product.increaseQuantity}
                    className="flex justify-center items-center w-5 focus:ring-offset-0 [&_svg_line]:stroke-brown-1 [&:disabled_svg_line]:stroke-grey-8"
                    disabled={
                      line.quantity >= maxOrderQTY?.max_order_qty ||
                      line.quantity >= maxQuantity ||
                      (isUpsellItem &&
                        upsellData.type === 'auto-add-product-type' &&
                        line.quantity === upsellData.maxQuantityAccessible)
                    }
                  >
                    <Plus />
                  </button>
                </div>

                {line.quantity < maxOrderQTY?.max_order_qty && line.quantity >= maxQuantity && (
                  <p className="text-[.8125rem] tracking-[.13px] leading-5 text-orange-1 mt-2">
                    {window.TMW.Strings.cart.reachedMaxAvailableQuantity}
                  </p>
                )}

                {maxOrderQTY && (
                  <MaxOrderMessage
                    maxOrder={maxOrderQTY.max_order_qty}
                    errorMessage={maxOrderQTY.error_message}
                    quantity={line.quantity}
                  />
                )}
              </div>
            )}

            {(!!discountValue || (isUpsellItem && !upsellData.showSelector)) && (
              <div className="flex h-[44px] border border-black mr-5">
                <div className="w-[102px] text-center text-headline-tag h-full flex items-center justify-center text-black">
                  {line.quantity}
                </div>
              </div>
            )}

            {isUpsellItem ? (
              upsellData.showRemoveLink && (
                <button onClick={() => handleRemove()} className="text-body-3 underline">
                  {window.TMW.Strings.cart.remove}
                </button>
              )
            ) : (
              <button onClick={() => handleRemove()} className="text-body-3 underline">
                {window.TMW.Strings.cart.remove}
              </button>
            )}
          </div>
        </div>

        {/* Bundle items */}
        {isBundle && (
          <>
            <button
              className="flex justify-between items-center py-2.5 px-5 text-body-3 w-full border border-grey-4 m-5 mb-0"
              onClick={() => setIsBundleOpen(!isBundleOpen)}
            >
              {isBundleOpen ? (
                <Angle />
              ) : (
                <div className="rotate-180">
                  <Angle />
                </div>
              )}
              {isBundleOpen ? window.TMW.Strings.cart.hide : window.TMW.Strings.cart.show} {line.children.length}{' '}
              {window.TMW.Strings.cart.productsInKit}
              {isBundleOpen ? (
                <Angle />
              ) : (
                <div className="rotate-180">
                  <Angle />
                </div>
              )}
            </button>
            {isBundleOpen && (
              <div className="flex flex-col mt-5">
                {line.children.map((child, index) => (
                  <div key={`line-${index}`} className="flex mb-4">
                    <a href={child.url} className="flex basis-[80px] max-w-[80px] lg:basis-[100px] lg:max-w-[100px]">
                      <Image
                        src={child.image}
                        classes="block object-contain w-[80px] h-[80px] lg:w-[100px] lg:h-[100px]"
                        alt={`${child.title} image`}
                      />
                    </a>
                    <div
                      className="flex flex-col basis-calc(100%-80px) max-w-[calc(100%-80px)]
                      lg:basis-[calc(100%-100px)] lg:max-w-[calc(100%-100px)] pl-2"
                    >
                      <a href={child.url}>
                        <h4 className="text-headline-tag hover:underline">{child.title}</h4>

                        <p className="text-body-light hover:underline">{child.properties?._short_description}</p>
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    ),
    [line, isBundleOpen, maxQuantity, upsells],
  );

  return lineItem;
};

export default withCartContext(CartItem);
